import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, Polygon, Polyline, useJsApiLoader } from "@react-google-maps/api";
import { envConfig } from "@/shared/services/apiConfig";
import * as h3 from "h3-js";
import {
  cellIdToH3Index,
  getCellCenter,
  getMapViewForPoints,
  headingDegrees,
  interpolateLatLng,
  H3_MAP_MAX_ZOOM,
  H3_MAP_MIN_ZOOM,
  H3_MAP_DEFAULT_ZOOM,
} from "@/shared/utils/h3-map-utils";
import { TerminalRoutePoint } from "@/shared/types/userTerminalTypes";

interface H3RouteMapProps {
  points: TerminalRoutePoint[];
  height?: string;
  fitOnResize?: boolean;
}

export const H3RouteMap = ({
  points,
  height = "300px",
  fitOnResize = false,
}: H3RouteMapProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const centerCacheRef = useRef<Map<string, google.maps.LatLngLiteral>>(new Map());
  const boundaryCacheRef = useRef<Map<string, google.maps.LatLngLiteral[]>>(new Map());
  const [center, setCenter] = useState<google.maps.LatLngLiteral>({ lat: 20, lng: 0 });
  const [mapZoom, setMapZoom] = useState<number>(H3_MAP_DEFAULT_ZOOM);
  const [isMapReady, setIsMapReady] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: envConfig.googleMapsApiKey,
  });

  const routeCoordinates = useMemo(() => {
    return points
      .map((point) => {
        try {
          const normalizedId = cellIdToH3Index(point.h3CellId);
          return getCellCenter(normalizedId, centerCacheRef.current);
        } catch {
          return null;
        }
      })
      .filter((point): point is google.maps.LatLngLiteral => Boolean(point));
  }, [points]);

  const directionMarkers = useMemo(() => {
    const markers: { key: string; position: google.maps.LatLngLiteral; heading: number }[] = [];

    for (let index = 0; index < routeCoordinates.length - 1; index += 1) {
      const from = routeCoordinates[index];
      const to = routeCoordinates[index + 1];
      if (from.lat === to.lat && from.lng === to.lng) continue;

      markers.push({
        key: `direction-${index}`,
        position: interpolateLatLng(from, to, 0.32),
        heading: headingDegrees(from, to),
      });
    }

    return markers;
  }, [routeCoordinates]);

  const hexagons = useMemo(() => {
    const seen = new Set<string>();
    const cells: { h3CellId: string; path: google.maps.LatLngLiteral[] }[] = [];

    for (const point of points) {
      try {
        const h3CellId = cellIdToH3Index(point.h3CellId);
        if (seen.has(h3CellId)) continue;
        seen.add(h3CellId);

        const cachedBoundary = boundaryCacheRef.current.get(h3CellId);
        const path =
          cachedBoundary ??
          (() => {
            const boundary = h3.cellToBoundary(h3CellId).map(([lat, lng]) => ({ lat, lng }));
            boundaryCacheRef.current.set(h3CellId, boundary);
            return boundary;
          })();

        cells.push({ h3CellId, path });
      } catch {
        // Skip invalid H3 cell ids.
      }
    }

    return cells;
  }, [points]);

  const hexagonOptions = useMemo(
    () =>
      ({
        fillColor: "#2563eb",
        fillOpacity: mapZoom >= 8 ? 0.16 : mapZoom >= 7 ? 0.22 : 0.3,
        strokeColor: "#1d4ed8",
        strokeWeight: mapZoom >= 8 ? 1 : 1.5,
        clickable: false,
        zIndex: 0,
      }) as google.maps.PolygonOptions,
    [mapZoom],
  );

  const mapView = useMemo(() => getMapViewForPoints(routeCoordinates), [routeCoordinates]);

  const googleMapOptions = useMemo(
    () => ({
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      minZoom: 2,
      restriction: {
        latLngBounds: {
          north: 85,
          south: -85,
          west: -180,
          east: 180,
        },
        strictBounds: true,
      },
    }),
    [],
  );

  const fitMapToRoute = useCallback(() => {
    if (!mapRef.current || routeCoordinates.length === 0) return;

    if (routeCoordinates.length === 1) {
      mapRef.current.setCenter(routeCoordinates[0]);
      mapRef.current.setZoom(H3_MAP_MIN_ZOOM);
      setCenter(routeCoordinates[0]);
      setMapZoom(H3_MAP_MIN_ZOOM);
      return;
    }

    if (mapView.isWideSpread) {
      mapRef.current.setCenter(mapView.center);
      mapRef.current.setZoom(mapView.zoom);
      setCenter(mapView.center);
      setMapZoom(mapView.zoom);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    routeCoordinates.forEach((point) => bounds.extend(point));
    mapRef.current.fitBounds(bounds, 48);

    google.maps.event.addListenerOnce(mapRef.current, "idle", () => {
      if (!mapRef.current) return;

      let finalZoom = mapRef.current.getZoom() ?? H3_MAP_MIN_ZOOM;
      if (finalZoom > H3_MAP_MAX_ZOOM) {
        finalZoom = H3_MAP_MAX_ZOOM;
      }
      if (finalZoom < H3_MAP_MIN_ZOOM) {
        finalZoom = H3_MAP_MIN_ZOOM;
      }

      mapRef.current.setCenter(mapView.center);
      mapRef.current.setZoom(finalZoom);
      setCenter(mapView.center);
      setMapZoom(finalZoom);
    });
  }, [mapView, routeCoordinates]);

  useEffect(() => {
    if (routeCoordinates.length > 0 && !isMapReady) {
      setCenter(mapView.center);
    }
  }, [isMapReady, mapView.center, routeCoordinates.length]);

  useEffect(() => {
    if (!isMapReady) return;
    fitMapToRoute();
  }, [fitMapToRoute, isMapReady]);

  useEffect(() => {
    if (!fitOnResize || !isMapReady) return;
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      if (!mapRef.current) return;
      google.maps.event.trigger(mapRef.current, "resize");
      fitMapToRoute();
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [fitMapToRoute, fitOnResize, isMapReady]);

  const startPoint = routeCoordinates[0];
  const endPoint = routeCoordinates.length > 1 ? routeCoordinates[routeCoordinates.length - 1] : null;

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center border rounded-md" style={{ height }}>
        Loading map...
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full h-full rounded-md border" style={{ height }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center}
        zoom={mapZoom}
        options={googleMapOptions}
        onLoad={(map) => {
          mapRef.current = map;
          setMapZoom(H3_MAP_DEFAULT_ZOOM);
          setIsMapReady(true);
        }}
        onZoomChanged={() => {
          if (!mapRef.current) return;
          const currentZoom = mapRef.current.getZoom();
          if (typeof currentZoom === "number" && currentZoom !== mapZoom) {
            setMapZoom(currentZoom);
          }
        }}
      >
        {hexagons.map((hexagon) => (
          <Polygon
            key={hexagon.h3CellId}
            path={hexagon.path}
            options={hexagonOptions}
          />
        ))}

        {routeCoordinates.length > 1 && (
          <Polyline
            path={routeCoordinates}
            options={{
              strokeColor: "#2563eb",
              strokeOpacity: 0.9,
              strokeWeight: 4,
              geodesic: true,
              zIndex: 1,
            }}
          />
        )}

        {directionMarkers.map((marker) => (
          <Marker
            key={marker.key}
            position={marker.position}
            clickable={false}
            title="Travel direction"
            icon={{
              path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: mapZoom >= 8 ? 5 : mapZoom >= 7 ? 4 : 3.5,
              rotation: marker.heading,
              fillColor: "#1d4ed8",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 1.25,
            }}
            zIndex={2}
          />
        ))}

        {startPoint && endPoint && (
          <Marker
            position={startPoint}
            label={{ text: "S", color: "#ffffff", fontWeight: "700" }}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "#16a34a",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            }}
            zIndex={3}
          />
        )}

        {endPoint && (
          <Marker
            position={endPoint}
            label={{ text: "E", color: "#ffffff", fontWeight: "700" }}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "#dc2626",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            }}
            zIndex={3}
          />
        )}
      </GoogleMap>
    </div>
  );
};